/**
 * Escolhas de tela lembradas por pessoa (visão "Tabela | Mapa", camadas do mapa…), neste navegador.
 *
 * - `<PrefScopeProvider scope={idDaPessoa}>`: a casca diz de quem são as escolhas (outra pessoa no mesmo navegador
 *   não herda as de quem saiu);
 * - `useRemembered(key, inicial, valido?)`: como useState, mas guarda em localStorage em `hive.pref.<pessoa>.<key>`;
 *   `key` deve dizer a tela e o assunto ("assets.view", "k8s.map.layers"); sem `key`, não guarda;
 * - `valido(v)`: confere o que voltou do armazenamento (opção que deixou de existir volta ao inicial).
 * Sem armazenamento (navegação privada, bloqueio), vale só nesta página.
 */
import { createContext, createElement, useCallback, useContext, useState, type ReactNode } from 'react';

const ScopeCtx = createContext<string>('anon');

export function PrefScopeProvider({ scope, children }: { scope: string | null | undefined; children: ReactNode }) {
  return createElement(ScopeCtx.Provider, { value: scope || 'anon' }, children);
}

export function prefKey(scope: string, key: string): string {
  return `hive.pref.${scope}.${key}`;
}

function read<T>(k: string, initial: T, valid?: (v: unknown) => boolean): T {
  try {
    const raw = window.localStorage.getItem(k);
    if (raw == null) return initial;
    const v = JSON.parse(raw) as unknown;
    return !valid || valid(v) ? (v as T) : initial;
  } catch {
    return initial;
  }
}

export function useRemembered<T>(key: string | undefined, initial: T, valid?: (v: unknown) => boolean): [T, (v: T) => void] {
  const scope = useContext(ScopeCtx);
  const k = key ? prefKey(scope, key) : null;
  const [state, setState] = useState<{ k: string | null; v: T }>(() => ({ k, v: k ? read(k, initial, valid) : initial }));
  // outra pessoa ou outra chave: relê
  const v = state.k === k ? state.v : k ? read(k, initial, valid) : initial;
  if (state.k !== k) setState({ k, v });
  const set = useCallback(
    (nv: T) => {
      setState({ k, v: nv });
      if (!k) return;
      try {
        window.localStorage.setItem(k, JSON.stringify(nv));
      } catch {
        /* só não lembra */
      }
    },
    [k],
  );
  return [v, set];
}
